// Mirrors AmsBookingReconciliationReport / AmsBookingReconciliationItem in Cms.Services.Database.
// Enums arrive as numbers, like every enum this API returns.

export interface ReconciliationItem {
  action: number
  result: number
  message: string
  calBookingUid: string | null
  requestId: string | null
  referenceCode: string | null
  start: string | null
  calStatus: string | null
  cmsStatus: number | null
}

export interface ReconciliationReport {
  outcome: number
  dryRun: boolean
  startedAt: string
  windowStart: string
  windowEnd: string
  calBookingsExamined: number
  cmsBookingsExamined: number
  skippedInFlight: number
  skippedReassigning: number
  message: string | null
  items: ReconciliationItem[]
}

/** AmsBookingReconciliationOutcome. */
export const OUTCOME = { completed: 0, alreadyRunning: 1, aborted: 2 } as const

/** ReconcileActionResult. */
export const RESULT = { planned: 0, done: 1, skipped: 2, failed: 3, reported: 4 } as const

/** ReconcileAction — what the reconciler does about one booking. */
export const RECONCILE_ACTIONS: Record<number, string> = {
  4: "Cancel in CMS",
  5: "Resolve scheduling link",
  6: "Needs a look",
  7: "Cancel in scheduling system",
  8: "Settle system error",
  9: "Adopt into CMS",
  10: "Record system error",
  11: "Cancel duplicate",
  12: "Double booking",
  13: "Record host",
  14: "Record reschedule",
}

const RESULT_LABELS: Record<number, string> = {
  0: "Would do",
  1: "Done",
  2: "Skipped",
  3: "Failed",
  4: "Reported",
}

export function actionLabel(action: number): string {
  return RECONCILE_ACTIONS[action] ?? `Action ${action}`
}

export function resultLabel(result: number): string {
  return RESULT_LABELS[result] ?? `Result ${result}`
}

/** Actions a dry run says it would take — what "Apply" will attempt. */
export function plannedCount(report: ReconciliationReport): number {
  return report.items.filter((item) => item.result === RESULT.planned).length
}

/** One previewed action, as POST api/ams/bookings/reconcile/apply takes it (AmsBookingReconciliationStepKey). */
export interface ReconciliationStepKey {
  action: number
  calBookingUid: string | null
  requestId: string | null
}

/**
 * What "Apply" sends: the preview's planned actions and nothing else, so the server takes only those.
 * Anything it finds that the preview did not list, it reports as skipped.
 */
export function plannedSteps(report: ReconciliationReport): ReconciliationStepKey[] {
  return report.items
    .filter((item) => item.result === RESULT.planned)
    .map(({ action, calBookingUid, requestId }) => ({ action, calBookingUid, requestId }))
}

/** One line saying what the run did, or why it did nothing. */
export function summarize(report: ReconciliationReport): string {
  if (report.outcome === OUTCOME.alreadyRunning) {
    return "Another reconciliation is already running. Try again in a minute."
  }

  if (report.outcome === OUTCOME.aborted) {
    return report.message ?? "The scheduling system could not be read completely, so nothing was changed."
  }

  const compared = `Compared ${report.calBookingsExamined} scheduling-system ${report.calBookingsExamined === 1 ? "booking" : "bookings"} and ${report.cmsBookingsExamined} booked CMS ${report.cmsBookingsExamined === 1 ? "row" : "rows"}.`
  const waiting = report.skippedInFlight + report.skippedReassigning
  const later = waiting > 0 ? ` ${waiting} changed moments ago or ${waiting === 1 ? "is" : "are"} being moved, and ${waiting === 1 ? "is" : "are"} left for the next pass.` : ""

  if (report.items.length === 0) {
    return `${compared} CMS and the scheduling system agree; nothing to do.${later}`
  }

  if (report.dryRun) {
    const planned = plannedCount(report)
    const reported = report.items.length - planned
    const parts = [`${planned} ${planned === 1 ? "change" : "changes"} would be made`]
    if (reported > 0) parts.push(`${reported} ${reported === 1 ? "item needs" : "items need"} a look`)
    return `${compared} ${parts.join("; ")}. Nothing has been changed yet.${later}`
  }

  const done = report.items.filter((item) => item.result === RESULT.done).length
  const failed = report.items.filter((item) => item.result === RESULT.failed).length
  const skipped = report.items.filter((item) => item.result === RESULT.skipped).length
  const parts = [`${done} done`]
  if (failed > 0) parts.push(`${failed} failed (tried again next pass)`)
  if (skipped > 0) parts.push(`${skipped} skipped after a fresh look`)
  return `${compared} ${parts.join(", ")}.${later}`
}
